/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Shield, Plane, Zap, Target, Award, Volume2, VolumeX, Pause, Play } from 'lucide-react';

interface GameHUDProps {
  health: number;
  maxHealth: number;
  shield: number;
  maxShield: number;
  missiles: number;
  maxMissiles: number;
  xp: number;
  xpNeeded: number;
  combo: number;
  score: number;
  bestScore: number;
  kills: number;
  level: number;
  difficulty: 'rookie' | 'veteran' | 'ace';
  isMuted: boolean;
  toggleMute: () => void;
  screen: 'playing' | 'paused';
  setScreen: (screen: 'playing' | 'paused') => void;
}

export const GameHUD: React.FC<GameHUDProps> = ({
  health,
  maxHealth,
  shield,
  maxShield,
  missiles,
  maxMissiles,
  xp,
  xpNeeded,
  combo,
  score,
  bestScore,
  kills,
  level,
  difficulty,
  isMuted,
  toggleMute,
  screen,
  setScreen,
}) => {
  const healthPct = Math.max(0, (health / maxHealth) * 100);
  const shieldPct = Math.max(0, (shield / maxShield) * 100);
  const xpPct = Math.max(0, (xp / xpNeeded) * 100);

  const getDifficultyColor = () => {
    if (difficulty === 'rookie') return 'text-emerald-400 bg-emerald-950/40 border-emerald-800/40';
    if (difficulty === 'veteran') return 'text-amber-400 bg-amber-950/40 border-amber-800/40';
    return 'text-rose-400 bg-rose-950/40 border-rose-800/40';
  };

  return (
    <div id="game-hud" className="w-full bg-slate-950/95 border border-slate-800 flex flex-col md:flex-row gap-4 p-4 rounded-xl text-slate-200 shadow-xl backdrop-blur-md">
      {/* 1. HEALTH AND SHIELD STATUS (LEFT SECTION) */}
      <div className="flex-1 space-y-3 min-w-[240px]">
        {/* Level and active health header */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="px-2.5 py-0.5 rounded text-xs font-mono font-bold bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
              LVL {level}
            </div>
            <div className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold tracking-wider uppercase border ${getDifficultyColor()}`}>
              {difficulty} SPEC
            </div>
          </div>
          <div className="font-mono text-xs text-slate-400">
            Kills: <span className="text-slate-100 font-bold">{kills}</span>
          </div>
        </div>

        {/* Health Bar */}
        <div className="space-y-1">
          <div className="flex justify-between text-xs font-mono">
            <span className="flex items-center gap-1 text-rose-400">
              <Plane className="w-3.5 h-3.5 fill-rose-400/20" /> HULL STABILITY
            </span>
            <span className="text-slate-300 font-bold">{Math.round(health)}%</span>
          </div>
          <div className="w-full bg-slate-900 border border-slate-800 rounded-full h-3 overflow-hidden">
            <div 
              className={`h-full transition-all duration-150 ${
                healthPct < 30 ? 'bg-gradient-to-r from-red-600 to-rose-500 animate-pulse' : 'bg-gradient-to-r from-rose-600 to-orange-500'
              }`}
              style={{ width: `${healthPct}%` }}
            />
          </div>
        </div>

        {/* Shield Bar */}
        <div className="space-y-1">
          <div className="flex justify-between text-xs font-mono">
            <span className="flex items-center gap-1 text-cyan-400">
              <Shield className="w-3.5 h-3.5 fill-cyan-400/20" /> BARRIER FIELD
            </span>
            <span className="text-slate-300 font-bold">{Math.round(shield)}%</span>
          </div>
          <div className="w-full bg-slate-900 border border-slate-800 rounded-full h-3 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-cyan-600 to-blue-400 transition-all duration-150"
              style={{ width: `${shieldPct}%` }}
            />
          </div>
        </div>

        {/* XP Progress Bar */}
        <div className="space-y-1">
          <div className="flex justify-between text-[11px] font-mono text-slate-400">
            <span>TRAINING RECON XP</span>
            <span>{xp} / {xpNeeded}</span>
          </div>
          <div className="w-full bg-slate-900/50 rounded-full h-1 overflow-hidden">
            <div 
              className="h-full bg-cyan-400 transition-all duration-300"
              style={{ width: `${xpPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* 2. WEAPONS CARRIER (CENTER COCKPIT CONTROLS) */}
      <div className="flex-1 flex flex-row gap-4 items-center justify-center border-y md:border-y-0 md:border-x border-slate-800/80 py-3 md:py-0 px-4">
        {/* Spec Homing Missiles stock indicator */}
        <div className="text-center space-y-1.5 w-1/2">
          <span className="block text-[11px] font-mono text-slate-400 tracking-wider">HOMING SEEKERS</span>
          <div className="flex justify-center gap-1">
            {Array.from({ length: maxMissiles }).map((_, idx) => (
              <div 
                key={idx}
                className={`w-2 h-6 rounded-sm border transition-all duration-250 ${
                  idx < missiles 
                    ? 'bg-gradient-to-t from-orange-600 to-yellow-400 border-orange-500 shadow-[0_0_6px_rgba(234,179,8,0.4)]'
                    : 'bg-slate-900 border-slate-800'
                }`}
              />
            ))}
          </div>
          <span className="block text-[10px] font-mono text-amber-400 text-center animate-pulse">
            [SPACE] ATOMIC MISSILE ({missiles})
          </span>
        </div>

        {/* Hot keys feedback indicators for control support */}
        <div className="flex flex-col gap-1 w-1/2 text-xs font-mono justify-center pl-2 border-l border-slate-800/40">
          <div className="flex items-center gap-2 text-slate-400">
            <span className="w-12 text-center text-[10px] bg-slate-800 text-slate-100 rounded px-1 py-0.5 border border-slate-700">SHIFT</span>
            <span className="text-[11px] text-slate-300">Boost Speed</span>
          </div>
          <div className="flex items-center gap-2 text-slate-400">
            <span className="w-12 text-center text-[10px] bg-slate-800 text-slate-100 rounded px-1 py-0.5 border border-slate-700">W/A/S/D</span>
            <span className="text-[11px] text-slate-300">Flight Steer</span>
          </div>
        </div>
      </div>

      {/* 3. COMBOS AND SCORE BOARDS (RIGHT PANEL) */}
      <div className="flex-1 flex flex-row items-center justify-between min-w-[220px] pl-2">
        {/* Score metrics */}
        <div className="space-y-1">
          <div className="font-mono text-[11px] text-slate-400 uppercase tracking-widest flex items-center gap-1">
            <Award className="w-3.5 h-3.5 text-yellow-500" /> SCORES
          </div>
          <div className="text-2xl font-mono font-black tracking-tight text-white bg-slate-900/40 px-3 py-1 rounded border border-slate-800">
            {score.toLocaleString()}
          </div>
          <div className="text-[10px] font-mono text-slate-500">
            TOP RECORD: <span className="text-yellow-500/80 font-bold">{bestScore.toLocaleString()}</span>
          </div>
        </div>

        {/* Combo Multiplier feedback widget */}
        <div className="flex flex-col items-center justify-center px-4">
          <div className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">COMBO</div>
          <div className={`text-3xl font-mono font-black tracking-tighter ${
            combo > 1 
              ? 'text-yellow-400 scale-110 drop-shadow-[0_0_8px_rgba(250,204,21,0.5)] animate-bounce' 
              : 'text-slate-600'
          }`}>
            x{combo}
          </div>
          {combo > 1 && (
            <span className="text-[9px] font-mono text-yellow-500 tracking-tight">MULTIPLIER</span>
          )}
        </div>

        {/* Pause and Audio control toggles */}
        <div className="flex flex-col gap-1">
          <button 
            id="hud-mute-btn"
            onClick={toggleMute}
            className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-800 text-slate-400 hover:text-white transition-all cursor-pointer"
            title={isMuted ? "Unmute sound" : "Mute sound"}
          >
            {isMuted ? <VolumeX className="w-4 h-4 text-rose-400" /> : <Volume2 className="w-4 h-4 text-emerald-400" />}
          </button>
          
          <button 
            id="hud-pause-btn"
            onClick={() => setScreen(screen === 'playing' ? 'paused' : 'playing')}
            className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-800 text-slate-400 hover:text-white transition-all cursor-pointer"
            title={screen === 'playing' ? "Pause Game" : "Resume Game"}
          >
            {screen === 'playing' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};
